import { Controller } from "@hotwired/stimulus"

/**
 * Polls the Vector Map page while the background projection (PCA +
 * diagnostics) is still computing, and stops when the user asks it to.
 *
 * The original `<meta http-equiv="refresh">` was uncontrollable (WCAG 2.2 SC
 * 2.2.1 / 2.2.4) and reloaded the CURRENT url, so ?refresh=1 made every poll
 * re-bust the cache forever. The first Stimulus replacement stopped on demand
 * but polled by FULL NAVIGATION (window.location.assign), which hard-reset
 * keyboard focus and screen-reader position on every tick (CONEK-6939 AC3)
 * and forgot the stop on reload (AC2).
 *
 * Now every tick is a morph-reload of the enclosing turbo-frame
 * (refresh="morph" — Idiomorph morphs the frame's children in place, so focus
 * and scroll survive), and a deliberate stop persists in sessionStorage for
 * the visit, one key per entity_type scope, cleared by ?refresh=1 for the
 * scope it re-computes (a fresh compute means poll again).
 */

// sessionStorage is per-tab, which is exactly "for the visit" — closing the
// tab resets it, sharing it with another tab does not. ONE KEY PER
// entity_type scope: clearing a stop must never touch another scope's entry,
// or "Stop" on the Item map followed by the page Refresh button on Task
// (its href carries ?entity_type=Task&refresh=1) would wipe the Item stop
// and resurrect polling for a map the user explicitly stopped. Every access
// is guarded: a throwing/unavailable store degrades to the pre-persistence
// behaviour.
const STOPPED_KEY = "ai.vector_map_poll.stopped"

export default class extends Controller {
  static targets = ["status", "stopButton"]
  static values = {
    interval: { type: Number, default: 4000 },
    // ~4 minutes at the default interval. VectorMapComputeJob is slow (>40s is
    // normal) but not unbounded; past this the honest answer is "something is
    // wrong", not another reload. The cap is NOT the user's intent, so it
    // stops the timer WITHOUT persisting anything.
    maxAttempts: { type: Number, default: 60 },
  }

  connect() {
    this.attempts = 0
    this.frame = this.element.closest("turbo-frame")
    this.#bindFrameLoad()
    if (this.#arrivedViaRefresh()) {
      this.#clearStopped()
    } else if (this.#stoppedForVisit()) {
      this.#applyStoppedState()
      return
    }
    this.#startTimer()
  }

  // A Turbo navigation away is NOT the user's intent — clear the timer, never
  // persist. Only stop() (the button) freezes polling for the visit.
  disconnect() {
    this.#unbindFrameLoad()
    this.#stopTimer()
  }

  tick() {
    if (this.attempts >= this.maxAttemptsValue) {
      // A cap is NOT the user's intent — never persist — but a dead card must
      // not keep looking live: show the stopped state, in memory only.
      this.#applyStoppedState()
      return
    }
    this.attempts += 1
    this.#reloadFrame()
  }

  /**
   * Wired to "Check now" — a fresh request to poll: it resumes the timer,
   * spends the persisted stop, and resets the attempt counter. The reload
   * morphs the id-matched card and re-syncs the server's attributes
   * (data-polling="true", stop button visible) in place, so the controller
   * MUST be polling again or the card would look live while nothing ran
   * (connect() is never re-run by a morph). Without resetting attempts, a
   * "check now" after the maxAttempts cap did one reload and died on the
   * very next tick.
   */
  check() {
    this.attempts = 0
    this.#clearStopped()
    this.#startTimer()
    this.#reloadFrame()
  }

  /** Wired to the "Stop refreshing" control — the Timing Adjustable escape. */
  stop() {
    this.#persistStopped()
    this.#applyStoppedState()
  }

  /**
   * Turbo fires "turbo:frame-load" on the frame after EVERY frame render —
   * reload ticks and the eager src load alike (FrameController dispatches it
   * post-render, MorphingFrameRenderer included). A morph re-syncs the
   * server-rendered attributes on the id-matched card, so a persisted stop
   * would otherwise be visually reversed: data-polling="true" and a visible
   * stop button while no timer runs. Re-assert the stopped state whenever
   * one is persisted for this scope.
   */
  #bindFrameLoad() {
    if (!this.frame) return
    this.frameLoadListener = () => this.#resyncAfterMorph()
    this.frame.addEventListener("turbo:frame-load", this.frameLoadListener)
  }

  #unbindFrameLoad() {
    if (this.frame && this.frameLoadListener) {
      this.frame.removeEventListener("turbo:frame-load", this.frameLoadListener)
      this.frameLoadListener = null
    }
  }

  #resyncAfterMorph() {
    if (this.#stoppedForVisit()) this.#applyStoppedState()
  }

  #startTimer() {
    if (this.timer) return
    this.timer = setInterval(() => this.tick(), this.intervalValue)
  }

  #stopTimer() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  #applyStoppedState() {
    this.#stopTimer()
    if (this.hasStopButtonTarget) this.stopButtonTarget.hidden = true
    if (this.hasStatusTarget) this.statusTarget.dataset.polling = "false"
  }

  /**
   * Morph-reload the enclosing frame instead of navigating the page.
   *
   * Without a frame ancestor there is NOTHING safe to fall back to — a full
   * navigation would resurface the focus bug (AC3) — so warn and skip: the
   * user keeps a working page and can hit "Check now" on a page where the
   * frame did render.
   */
  #reloadFrame() {
    const frame = this.element.closest("turbo-frame")
    if (frame && typeof frame.reload === "function") {
      frame.reload()
    } else {
      console.warn(
        "[ai--vector-map-poll] no turbo-frame ancestor; poll tick skipped",
      )
    }
  }

  #persistStopped() {
    try {
      window.sessionStorage.setItem(this.#stoppedKey(), this.#scope())
    } catch {
      /* storage unavailable — the stop still applies to this page */
    }
  }

  #stoppedForVisit() {
    try {
      return window.sessionStorage.getItem(this.#stoppedKey()) === this.#scope()
    } catch {
      return false
    }
  }

  #clearStopped() {
    try {
      window.sessionStorage.removeItem(this.#stoppedKey())
    } catch {
      /* storage unavailable — nothing to clear */
    }
  }

  // The entity_type this page is filtered by — the same param the server
  // scopes the compute cache with.
  #scope() {
    return new URLSearchParams(window.location.search).get("entity_type") ?? ""
  }

  #stoppedKey() {
    return `${STOPPED_KEY}.${this.#scope()}`
  }

  #arrivedViaRefresh() {
    return new URLSearchParams(window.location.search).has("refresh")
  }
}
